'use client'
import { useMemo } from 'react'
import { Info } from 'lucide-react'
import { useTranslations, useLocale } from 'next-intl'
import Image from 'components/commercetools-ui/atoms/image'
import Link from 'components/commercetools-ui/atoms/link'
import { useAccountContext } from 'context/account'
import { getProductLoyaltyPoints } from 'helpers/loyalty'
import { Product } from 'types/entity/product'
import LoyaltyPointsBadge from '../../atoms/loyalty-points-badge'
import AddToCart from '../product-tile/add-to-cart'

export interface PremiumTileProps {
  product: Product;
  classNames?: string
}

const PremiumTile = ({
  product,
  classNames,
}: PremiumTileProps) => {
  const t = useTranslations()
  const locale = useLocale()
  const {
    availableLoyaltyPoints,
    loyaltyPointsLoading,
  } = useAccountContext()

  const selectedVariant = product?.variants[0]
  const productLoyaltyPointsPrice = selectedVariant?.attributes
    ? getProductLoyaltyPoints(selectedVariant.attributes, locale)
    : 0
  const canAddToCart = !loyaltyPointsLoading && availableLoyaltyPoints !== null && availableLoyaltyPoints >= productLoyaltyPointsPrice

  const productUrl = useMemo(() => {
    const searchParams = new URLSearchParams()
    const search = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return product._url ? product._url + search : undefined
  }, [product, selectedVariant])

  return (
    <div className={`max-w-450 flex flex-col justify-between rounded-lg border border-gray-200 bg-white p-8 ${classNames}`}>
      <Link link={productUrl} tabIndex={-1} title={product?.name}>
        <div className="mb-16 w-full">
          <Image
            src={selectedVariant.images?.[0] ?? '/images/product-image-placeholder.svg'}
            suffix={selectedVariant?.images?.[0] ? 'medium' : ''}
            alt={product?.name ?? ''}
            simpleTitle={product?.name ?? ''}
            width={600}
            height={600}
            style={{ width: '100%', height: 'auto', aspectRatio: '1', objectFit: 'contain', objectPosition: 'center' }}
            sizes='50vw'
          />
        </div>
        <LoyaltyPointsBadge points={productLoyaltyPointsPrice}/>
        <h3 className='font-body mb-16 text-sm font-bold text-gray-900'>{product.name}</h3>
        {
          (availableLoyaltyPoints !== null && availableLoyaltyPoints < productLoyaltyPointsPrice) &&
            <p className='mb-16 flex items-center justify-start gap-8 text-xs font-normal text-gray-600'>
              <Info/>
              {t('account.loyalty.missing-points', {
                points: (productLoyaltyPointsPrice - availableLoyaltyPoints).toLocaleString(locale),
              })}
            </p>
        }
      </Link>
      <div className='flex flex-col items-end'>
        <AddToCart
          variant={selectedVariant}
          product={product}
          maxPurchaseQuantity={canAddToCart ? selectedVariant.availableQuantity : 0}
        />
      </div>
    </div>
  )
}

export default PremiumTile
